import { Component, EventEmitter, Inject, Output, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { MyConfigOrganization } from '../../../../../models/myConfig';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { EditOrganizationParkingPricingRequest } from '../../../../../models/orderModel';

@Component({
  selector: 'app-parking-pricing-form',
  standalone: false,
  templateUrl: './parking-pricing-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './parking-pricing-form.component.css'
})
export class ParkingPricingFormComponent {
  @Output() saveChangesEvent = new EventEmitter<void>();
  isLoading: boolean = false;
  parkingPricingForm: UntypedFormGroup;
  componentDestroyed$: Subject<boolean> = new Subject();
  activeRequests: number = 0;
  activeOptions: any = [
    { value: true, label: 'Active' },
    { value: false, label: 'Inactive' }
  ];

  constructor(
    private fb: UntypedFormBuilder,
    private BackOfficeService: BackOfficeService,
    public snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: {
      parkingPricing: EditOrganizationParkingPricingRequest,
      organization: MyConfigOrganization,
      edit: boolean,
    },
    @Inject(MatDialogRef<ParkingPricingFormComponent>) private dialogRef: MatDialogRef<ParkingPricingFormComponent>
  ) {
    this.initEmptyPricingForm();
  }

  ngOnInit(): void {
    this.data.parkingPricing ? this.initPricingForm() : this.initEmptyPricingForm();
  }

  setSwedishTimeZone(controlName: string, event: any) {
    const selectedDate = new Date(event.value);
    if (selectedDate) {

      const swedishOffset = -new Date().getTimezoneOffset();
      const swedishDate = new Date(selectedDate.getTime() + swedishOffset * 60 * 1000);
      this.parkingPricingForm.get(controlName)?.setValue(swedishDate.toISOString().replace('Z', ''));
    } else {
      this.parkingPricingForm.get(controlName)?.setValue(null);
    }
  }

  private setLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  private incrementRequests() {
    this.activeRequests++;
    this.setLoadingState(true);
  }

  private decrementRequests() {
    if (this.activeRequests > 0)
      this.activeRequests--;
    if (this.activeRequests === 0) {
      this.setLoadingState(false);
    }
  }

  submitForm() {
    this.incrementRequests();

    var model: EditOrganizationParkingPricingRequest = this.createParkingPricingPayload();

    this.BackOfficeService
      .saveOrganizationParkingPrice(this.data.organization.organizationId, model)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (ParkingPrice) => {
          this.snackBar.open('Parking price created: ' + ParkingPrice.id, 'Success', {
            verticalPosition: 'top',
            duration: 2000
          });
          this.decrementRequests();
          this.saveChangesEvent.emit(ParkingPrice);
          this.dialogRef.close();
        },
        error: (err) => {
          this.snackBar.open('Failed to create parking price: ' + err.message, 'Error', {
            verticalPosition: 'top',
            duration: 2000
          });
          this.decrementRequests();
        }
      });
  }

  createParkingPricingPayload() {
    return {
      id: this.parkingPricingForm.controls['id'].value !== '' ? this.parkingPricingForm.controls['id'].value : null,
      name: this.parkingPricingForm.controls['name'].value,
      code: this.parkingPricingForm.controls['code'].value,
      description: this.parkingPricingForm.controls['description'].value,
      price: this.parkingPricingForm.controls.totalPrice.value/(1 + this.parkingPricingForm.controls.vat.value),
      vat: this.parkingPricingForm.controls['vat'].value,
      active: this.parkingPricingForm.controls['active'].value
    };
  }

  initPricingForm() {
    this.parkingPricingForm = this.fb.group({
      id: [{ value: this.data.parkingPricing.id || '', disabled: (this.data.edit || this.isLoading) }],
      name: [{ value: this.data.parkingPricing.name || '', disabled: (this.data.edit || this.isLoading) }, Validators.required],
      code: [{ value: this.data.parkingPricing.code || '', disabled: (this.data.edit || this.isLoading) }, Validators.required],
      description: [{ value: this.data.parkingPricing.description || '', disabled: (this.data.edit || this.isLoading) }],
      price: [{ value: 0 || '', disabled: true }, Validators.required],
      vat: [{ value: this.data.parkingPricing.vat / 100, disabled: true }],
      totalPrice: [{ value: 0 || '', disabled: false }, Validators.required],
      active: [{ value: this.data.parkingPricing.active || '', disabled: (this.data.edit || this.isLoading) }],
    });
    this.parkingPricingForm.controls.price.setValue(this.data.parkingPricing.price, { emitEvent: false });
    this.parkingPricingForm.controls.totalPrice.setValue(this.data.parkingPricing.price * (1 + (this.data.parkingPricing.vat/100)), { emitEvent: false });
  }

  initEmptyPricingForm() {
    this.parkingPricingForm = this.fb.group({
      id: [{ value: '', disabled: this.isLoading }],
      name: [{ value: '', disabled: this.isLoading }, Validators.required],
      code: [{ value: '', disabled: this.isLoading }, Validators.required],
      description: [{ value: '', disabled: this.isLoading }],
      price: [{ value: 0, disabled: true }, Validators.required],
      vat: [{ value: 0.25, disabled: false }],
      totalPrice: [{ value: 0 || '', disabled: true }, Validators.required],
      active: [{ value: '', disabled: this.isLoading }],
    });
  }

  // clearDate(event, value) {
  //   event.stopPropagation();
  //   value.setValue('', { emitEvent: false });
  // }

}
